// ContextPanel — Conversation → Details / Edit. One place, clear sections:
// General · Members (roles) · Meetings · Notifications · Capabilities ·
// Manage (danger zone). Authorization mirrors the backend's object-scoped
// model (OWNER/ADMIN manage; members manage only themselves) — buttons are
// hidden for clarity, the server is what actually refuses. Capabilities are
// entitlement-driven (tenant products), never hardcoded per tenant.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Video, CalendarClock, Shield, Bell, Puzzle, AlertTriangle, Pencil, Check, X, Smile } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { DANA_BOT_USER_ID, type Availability, type ConversationSummary, type ConversationMember, type MeetingProviders, type NotificationLevel } from '../types';
import type { Directory } from '../hooks/useDirectory';
import { useAvailability } from '../hooks/useAvailability';
import { conversationsApi } from '../api/conversationsApi';
import { MeetingComposer } from './MeetingComposer';
import { CirclePlaylist } from './CirclePlaylist';
import { AvailabilityBadge, AvailabilityDot } from './AvailabilityBadge';
import { StatusDialog } from './StatusDialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';

/** Tenant capabilities available in this conversation (entitlement-driven). */
export interface ConversationCapabilities {
  work?: boolean;
  recognition?: boolean;
  meetings?: boolean;
  music?: boolean;
  files?: boolean;
  dana?: boolean;
}

interface Props {
  conversation: ConversationSummary;
  directory: Directory;
  selfId?: string;
  canManage?: boolean;
  onResync?: () => void;
  onChanged?: () => void; // refresh the inbox/thread after a membership/name edit
  onClose: () => void;
  canConfigureIntegrations?: boolean; // admin/hr → "Configure integrations" CTA
  meetingSignal?: number;             // header video quick-action bumps this to open the composer
  onMeetingScheduled?: () => void;    // refresh the thread after a meeting card posts
  capabilities?: ConversationCapabilities;
  onLeftOrArchived?: () => void;      // navigate away after leave/archive
}

const SectionTitle: React.FC<{ icon?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }> = ({ icon, children, right }) => (
  <div className="mb-2 flex items-center justify-between">
    <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {icon}
      {children}
    </h4>
    {right}
  </div>
);

// CircleMeetings — Details → Meetings (and the target of the header video
// quick-action). Discovers providers lazily, then offers Schedule / Meet now,
// both opening the SAME MeetingComposer (one reusable flow). Only providers the
// tenant actually enabled are shown (the API decides — never a hardcoded list).
const CircleMeetings: React.FC<{
  conversation: ConversationSummary;
  memberCount: number;
  meetingSignal?: number;
  canConfigureIntegrations?: boolean;
  onScheduled?: () => void;
}> = ({ conversation, memberCount, meetingSignal, canConfigureIntegrations, onScheduled }) => {
  const { t } = useTranslation('conversations');
  const [providers, setProviders] = useState<MeetingProviders | undefined>();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<false | 'schedule' | 'now'>(false);
  const circleName = conversation.title || t('meeting.this_circle', { defaultValue: 'Circle' });

  const load = () => {
    if (providers || loading) return;
    setLoading(true);
    conversationsApi
      .getMeetingProviders(conversation.id)
      .then(setProviders)
      .catch(() => setProviders({ enabled: false, default: null, providers: [], count: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (meetingSignal && meetingSignal > 0) {
      load();
      setOpen('schedule');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingSignal]);

  const schedule = async (
    input: { title: string; startAt: string; endAt?: string; description?: string; timezone: string; provider?: string; generateLink: boolean },
  ) => {
    await conversationsApi.scheduleMeeting(conversation.id, {
      title: input.title, startAt: input.startAt, endAt: input.endAt, description: input.description,
      timezone: input.timezone, provider: input.provider, generateLink: input.generateLink,
    });
    onScheduled?.();
  };

  const enabledProviders = (providers?.providers ?? []).filter((p) => p.connected || p.can_create_link || (p as any).org_links);
  const canMeetNow = enabledProviders.some((p) => p.can_create_link && p.capabilities.includes('createInstantMeeting'));

  return (
    <section className="border-t border-border px-4 py-3">
      <SectionTitle icon={<CalendarClock className="h-3.5 w-3.5" />}>{t('details_view.meetings', { defaultValue: 'Meetings' })}</SectionTitle>
      {providers && enabledProviders.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {enabledProviders.map((p) => (
            <span key={p.provider} className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
              {p.label || p.provider}{providers.default === p.provider ? ` · ${t('meeting.default', { defaultValue: 'Default' })}` : ''}
            </span>
          ))}
        </div>
      )}
      {providers && enabledProviders.length === 0 && !loading && (
        <p className="mb-2 text-xs text-muted-foreground">{t('details_view.providers_none', { defaultValue: 'No meeting provider is enabled for your company.' })}</p>
      )}
      <div className="relative flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => { load(); setOpen(open === 'schedule' ? false : 'schedule'); }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm text-charcoal hover:bg-muted"
        >
          <CalendarClock className="h-4 w-4" /> {t('meeting.schedule', { defaultValue: 'Schedule meeting' })}
        </button>
        {canMeetNow && (
          <button
            type="button"
            onClick={() => { load(); setOpen(open === 'now' ? false : 'now'); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm text-charcoal hover:bg-muted"
          >
            <Video className="h-4 w-4" /> {t('meeting.meet_now', { defaultValue: 'Meet now' })}
          </button>
        )}
        {open && (
          <MeetingComposer
            circleName={circleName}
            memberCount={memberCount}
            providers={providers}
            loadingProviders={loading}
            meetNow={open === 'now'}
            canConfigureIntegrations={!!canConfigureIntegrations}
            onCreate={schedule}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{t('details_view.upcoming_meetings', { defaultValue: 'Upcoming meetings appear in the thread as meeting cards.' })}</p>
    </section>
  );
};

const MemberRow: React.FC<{
  name: string; sub?: string; avatarUrl?: string; you?: boolean; availability?: Availability; role: string; roleLabel: string;
  actions?: React.ReactNode;
}> = ({ name, sub, avatarUrl, you, availability, role, roleLabel, actions }) => (
  <li className="flex items-center gap-2 py-1.5">
    <span className="relative flex-none">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
          {name.slice(0, 2).toUpperCase()}
        </span>
      )}
      <AvailabilityDot availability={availability} size={10} className="absolute -bottom-0.5 -right-0.5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5 text-sm text-charcoal">
        <span className="truncate">{name}</span>
        <AvailabilityBadge availability={availability} />
        {you && <span className="text-xs text-muted-foreground">(you)</span>}
        {role !== 'MEMBER' && (
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${role === 'OWNER' ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/[0.15] dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>{roleLabel}</span>
        )}
      </span>
      {sub && <span className="block truncate text-xs text-muted-foreground">{sub}</span>}
    </span>
    {actions}
  </li>
);

export const ContextPanel: React.FC<Props> = ({
  conversation, directory, selfId, canManage, onResync, onChanged, onClose, canConfigureIntegrations, meetingSignal, onMeetingScheduled, capabilities, onLeftOrArchived,
}) => {
  const { t } = useTranslation('conversations');
  const d = (key: string, dflt: string, opts: Record<string, unknown> = {}) => t(`details_view.${key}`, { defaultValue: dflt, ...opts });
  const isSpace = ['TEAM', 'DEPARTMENT', 'COMPANY'].includes(conversation.type);
  const isGroup = conversation.type === 'GROUP';
  const TYPE_LABEL: Record<string, string> = {
    DIRECT: t('direct_message', { defaultValue: 'Direct message' }),
    GROUP: t('meeting.this_circle', { defaultValue: 'Circle' }),
    TEAM: 'Team space', DEPARTMENT: 'Department space', COMPANY: 'Company space', CONTEXTUAL: 'Linked conversation',
  };

  // Members with roles come from the API (roles are server truth); the inbox's
  // participant ids remain the fallback until they load.
  const [members, setMembers] = useState<ConversationMember[] | null>(null);
  const loadMembers = () => conversationsApi.getMembers(conversation.id).then(setMembers).catch(() => setMembers(null));
  useEffect(() => { setMembers(null); loadMembers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [conversation.id, conversation.participant_user_ids.length]);
  const memberIds = members ? members.map((m) => m.user_id) : conversation.participant_user_ids;
  // One batched availability read for the whole member list — it supersedes the old
  // presence-only dot (the payload carries `online` too).
  const availabilityByUser = useAvailability(memberIds.filter((id) => id !== DANA_BOT_USER_ID));
  const selfRole = members?.find((m) => m.user_id === selfId)?.role ?? (conversation.created_by === selfId ? 'OWNER' : 'MEMBER');
  const isOwner = selfRole === 'OWNER';
  const isManager = isOwner || selfRole === 'ADMIN' || (!!canManage && isSpace);
  const canEditDetails = (isGroup || isSpace) && isManager;
  const canEditMembers = isGroup && isManager;

  const [busy, setBusy] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const myAvailability = selfId ? availabilityByUser[selfId] : undefined;
  const confirm = useConfirm();
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await loadMembers();
      onChanged?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, d('not_allowed', "You don't have permission to do that.")));
    } finally {
      setBusy(false);
    }
  };

  // General
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState(conversation.title ?? '');
  const [descInput, setDescInput] = useState(conversation.description ?? '');
  const [policyInput, setPolicyInput] = useState(conversation.posting_policy);
  useEffect(() => { setNameInput(conversation.title ?? ''); setDescInput(conversation.description ?? ''); setPolicyInput(conversation.posting_policy); }, [conversation.id, conversation.title, conversation.description, conversation.posting_policy]);
  const saveGeneral = () => run(async () => {
    await conversationsApi.updateDetails(conversation.id, { title: nameInput.trim(), description: descInput.trim(), posting_policy: policyInput });
    setEditing(false);
  }, d('saved', 'Details saved'));

  // Members
  const [adding, setAdding] = useState(false);
  const [addQ, setAddQ] = useState('');
  const addable = directory.search(addQ).filter((p) => p.userId !== selfId && !memberIds.includes(p.userId)).slice(0, 20);
  const roleLabel = (r: string) => (r === 'OWNER' ? d('role_owner', 'Owner') : r === 'ADMIN' ? d('role_admin', 'Admin') : r === 'BOT' ? d('role_bot', 'AI assistant') : d('role_member', 'Member'));

  // Notifications (mine)
  const myLevel = (members?.find((m) => m.user_id === selfId)?.notification_level ?? 'ALL') as NotificationLevel | 'INHERIT';
  const setLevel = (level: NotificationLevel) => run(() => conversationsApi.setNotifications(conversation.id, level), d('notify_saved', 'Notification preference saved'));

  // Capabilities (entitlement-driven)
  const caps = useMemo(() => ([
    ['work', capabilities?.work, d('cap_work', 'Work')],
    ['recognition', capabilities?.recognition, d('cap_recognition', 'Recognition')],
    ['meetings', capabilities?.meetings !== false && conversation.type !== 'CONTEXTUAL', d('cap_meetings', 'Meetings')],
    ['music', capabilities?.music, d('cap_music', 'Music')],
    ['files', capabilities?.files !== false, d('cap_files', 'Files')],
    ['dana', capabilities?.dana !== false, d('cap_dana', 'Dana')],
  ] as const).filter(([, on]) => !!on), [capabilities, conversation.type]); // eslint-disable-line react-hooks/exhaustive-deps

  const humanCount = memberIds.filter((id) => id !== DANA_BOT_USER_ID).length;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-none items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-charcoal">{d('title', 'Details')}</h3>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-charcoal" aria-label={t('close', { defaultValue: 'Close' })}>✕</button>
      </header>

      <div className="flex-1 overflow-y-auto pb-24">
        {/* General */}
        <section className="px-4 py-3">
          <SectionTitle
            right={canEditDetails && !editing ? (
              <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"><Pencil className="h-3 w-3" />{t('edit.edit', { defaultValue: 'Edit' })}</button>
            ) : null}
          >
            {d('general', 'General')}
          </SectionTitle>
          <div className="mb-2 flex items-center gap-2">
            {conversation.avatar_file_ref ? (
              <img src={conversation.avatar_file_ref} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                {(conversation.title || TYPE_LABEL[conversation.type] || '?').slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-charcoal">{conversation.title || (isGroup ? d('unnamed', 'Unnamed circle') : TYPE_LABEL[conversation.type])}</p>
              <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{TYPE_LABEL[conversation.type] ?? conversation.type}</span>
            </div>
          </div>
          {editing ? (
            <div className="space-y-2">
              <label className="block text-xs text-muted-foreground">{d('name', 'Name')}
                <input value={nameInput} onChange={(e) => setNameInput(e.target.value)} className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal" />
              </label>
              <label className="block text-xs text-muted-foreground">{d('description', 'Description')}
                <textarea value={descInput} onChange={(e) => setDescInput(e.target.value)} rows={3} placeholder={d('description_placeholder', 'What is this conversation for?')} className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal" />
              </label>
              <label className="block text-xs text-muted-foreground">{d('posting_policy', 'Who can post')}
                <select value={policyInput} onChange={(e) => setPolicyInput(e.target.value as any)} className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal">
                  <option value="ALL_MEMBERS">{d('posting_all', 'All members')}</option>
                  <option value="ADMINS_ONLY">{d('posting_admins', 'Admins and owner only')}</option>
                  <option value="OWNERS_ONLY">{d('posting_owners', 'Owner only')}</option>
                </select>
              </label>
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={saveGeneral} className="inline-flex items-center gap-1 rounded bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white"><Check className="h-3 w-3" />{d('save', 'Save')}</button>
                <button type="button" onClick={() => setEditing(false)} className="inline-flex items-center gap-1 rounded border border-border px-2.5 py-1 text-xs text-charcoal"><X className="h-3 w-3" />{d('cancel', 'Cancel')}</button>
              </div>
            </div>
          ) : (
            <>
              {conversation.description && <p className="mb-2 whitespace-pre-wrap text-xs text-charcoal">{conversation.description}</p>}
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-muted-foreground">{d('visibility', 'Visibility')}</dt>
                <dd className="text-charcoal">{isSpace ? d('visibility_org', 'Synced from your organization') : d('visibility_private', 'Members only')}</dd>
                <dt className="text-muted-foreground">{d('posting_policy', 'Who can post')}</dt>
                <dd className="text-charcoal">{conversation.posting_policy === 'ADMINS_ONLY' ? d('posting_admins', 'Admins and owner only') : conversation.posting_policy === 'OWNERS_ONLY' ? d('posting_owners', 'Owner only') : d('posting_all', 'All members')}</dd>
              </dl>
              {isSpace && conversation.context_type && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {d('visibility_org', 'Synced from your organization')}.
                  {canManage && onResync && (
                    <button type="button" onClick={onResync} className="ml-1 font-medium text-primary-600 hover:underline">Re-sync now</button>
                  )}
                </p>
              )}
            </>
          )}
        </section>

        {/* Your availability — the courtesy signal colleagues see next to your name. */}
        <section className="border-t border-border px-4 py-3">
          <SectionTitle icon={<Smile className="h-3.5 w-3.5" />}>{t('availability_your_status', { defaultValue: 'Your availability' })}</SectionTitle>
          <div className="flex items-center gap-2">
            <AvailabilityDot availability={myAvailability} ring={false} />
            <span className="min-w-0 flex-1 truncate text-sm text-charcoal">
              {myAvailability ? `${myAvailability.label}${myAvailability.detail ? ` · ${myAvailability.detail}` : ''}` : t('availability_none', { defaultValue: 'No status set' })}
            </span>
            <button type="button" onClick={() => setStatusOpen(true)} className="flex-none text-xs font-medium text-primary-600 hover:underline">
              {t('availability_menu', { defaultValue: 'Set your status' })}
            </button>
          </div>
        </section>

        {/* Members */}
        <section className="border-t border-border px-4 py-3">
          <SectionTitle
            right={canEditMembers ? (
              <button type="button" onClick={() => setAdding((v) => !v)} className="text-xs font-medium text-primary-600 hover:underline">{adding ? d('done', 'Done') : `+ ${d('add_people', 'Add people')}`}</button>
            ) : null}
          >
            {t('details_view.member', { count: memberIds.length, defaultValue: '{{count}} members' })}
          </SectionTitle>
          {adding && canEditMembers && (
            <div className="mb-2 rounded-lg border border-border p-1.5">
              <input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder={d('search_people', 'Search people…')} className="mb-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal" />
              <ul className="max-h-40 overflow-y-auto">
                {addable.length === 0 ? (
                  <li className="px-1 py-2 text-xs text-muted-foreground">{d('no_one_to_add', 'No one to add.')}</li>
                ) : addable.map((p) => (
                  <li key={p.userId}>
                    <button type="button" disabled={busy} onClick={() => run(() => conversationsApi.addMembers(conversation.id, [p.userId]))} className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-muted disabled:opacity-50">
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">{p.name.slice(0, 2).toUpperCase()}</span>
                      <span className="truncate text-sm text-charcoal">{p.name}</span>
                      {p.position && <span className="truncate text-xs text-muted-foreground">· {p.position}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="divide-y divide-border">
            {(members ?? conversation.participant_user_ids.map((uid) => ({ user_id: uid, role: uid === conversation.created_by ? 'OWNER' : uid === DANA_BOT_USER_ID ? 'BOT' : 'MEMBER', membership_source: '', joined_at: '', notification_level: 'ALL' } as ConversationMember))).map((m) => {
              const uid = m.user_id;
              const p = directory.personOf(uid);
              const isDana = uid === DANA_BOT_USER_ID || m.role === 'BOT';
              const isSelf = uid === selfId;
              const name = isDana ? 'Dana' : m.name || directory.nameOf(uid);
              const removable = canEditMembers && !isSelf && !isDana && m.role !== 'OWNER';
              const canRole = isOwner && isGroup && !isSelf && !isDana && m.role !== 'OWNER';
              return (
                <MemberRow
                  key={uid}
                  name={name}
                  sub={isDana ? d('role_bot', 'AI assistant') : p?.position || p?.email}
                  avatarUrl={p?.avatarUrl}
                  you={isSelf}
                  availability={isDana ? undefined : availabilityByUser[uid]}
                  role={m.role}
                  roleLabel={roleLabel(m.role)}
                  actions={(removable || canRole) ? (
                    <span className="ml-auto flex flex-none items-center gap-1">
                      {canRole && (
                        <button type="button" disabled={busy} onClick={() => run(() => conversationsApi.setMemberRole(conversation.id, uid, m.role === 'ADMIN' ? 'MEMBER' : 'ADMIN'))} className="rounded px-1 text-[11px] text-primary-600 hover:underline" title={m.role === 'ADMIN' ? d('remove_admin', 'Remove admin') : d('make_admin', 'Make admin')}>
                          {m.role === 'ADMIN' ? d('remove_admin', 'Remove admin') : d('make_admin', 'Make admin')}
                        </button>
                      )}
                      {canRole && (
                        <button type="button" disabled={busy} onClick={async () => { if (await confirm({ title: d('transfer_ownership', 'Transfer ownership'), message: d('transfer_confirm', 'Make {{name}} the owner? You will stay as an admin.', { name }), confirmLabel: d('transfer_ownership', 'Transfer ownership') })) run(() => conversationsApi.transferOwnership(conversation.id, uid)); }} className="rounded px-1 text-[11px] text-muted-foreground hover:underline" title={d('transfer_ownership', 'Transfer ownership')}>
                          <Shield className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {removable && (
                        <button type="button" disabled={busy} onClick={() => run(() => conversationsApi.removeMember(conversation.id, uid))} aria-label={`${d('remove', 'Remove')} ${name}`} className="rounded px-1 text-muted-foreground hover:text-red-600">✕</button>
                      )}
                    </span>
                  ) : undefined}
                />
              );
            })}
          </ul>
        </section>

        {/* Meetings — tenant-enabled providers only */}
        {conversation.type !== 'CONTEXTUAL' && capabilities?.meetings !== false && (
          <CircleMeetings conversation={conversation} memberCount={humanCount} meetingSignal={meetingSignal} canConfigureIntegrations={canConfigureIntegrations} onScheduled={onMeetingScheduled} />
        )}

        {/* Notifications — existing per-member preference, no second system */}
        <section className="border-t border-border px-4 py-3">
          <SectionTitle icon={<Bell className="h-3.5 w-3.5" />}>{d('notifications', 'Notifications')}</SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            {(['ALL', 'MENTIONS', 'NONE'] as NotificationLevel[]).map((lvl) => (
              <button key={lvl} type="button" disabled={busy} onClick={() => setLevel(lvl)} className={`rounded-full border px-2.5 py-1 text-xs ${myLevel === lvl ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/[0.12]' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                {lvl === 'ALL' ? d('notify_all', 'All activity') : lvl === 'MENTIONS' ? d('notify_mentions', 'Mentions only') : d('notify_none', 'Muted')}
              </button>
            ))}
          </div>
        </section>

        {/* Capabilities — entitlement/config driven */}
        <section className="border-t border-border px-4 py-3">
          <SectionTitle icon={<Puzzle className="h-3.5 w-3.5" />}>{d('integrations', 'Capabilities')}</SectionTitle>
          <p className="mb-1.5 text-xs text-muted-foreground">{d('integrations_hint', "What this conversation can do, based on your company's products.")}</p>
          <div className="flex flex-wrap gap-1.5">
            {caps.map(([key, , label]) => (
              <span key={key} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-charcoal">{label}</span>
            ))}
          </div>
          {['GROUP', 'TEAM', 'DEPARTMENT', 'COMPANY'].includes(conversation.type) && capabilities?.music && (
            <div className="mt-3"><CirclePlaylist conversationId={conversation.id} circleName={conversation.title || 'Circle'} /></div>
          )}
        </section>

        {/* Manage / danger zone */}
        {(isGroup || (isSpace && isOwner)) && (
          <section className="border-t border-border px-4 py-3">
            <SectionTitle icon={<AlertTriangle className="h-3.5 w-3.5 text-red-600" />}>{d('danger_zone', 'Danger zone')}</SectionTitle>
            <div className="rounded-lg border border-red-200 p-2 dark:border-red-500/40">
              <div className="flex flex-col gap-1.5">
                {isGroup && (
                  <button type="button" disabled={busy} onClick={async () => { if (await confirm({ title: d('leave', 'Leave conversation'), message: d('leave_confirm', "Leave this conversation? You'll stop receiving its messages."), danger: true, confirmLabel: d('leave', 'Leave conversation') })) run(async () => { await conversationsApi.leave(conversation.id); onLeftOrArchived?.(); }, d('left', 'You left the conversation')); }} className="text-left text-sm text-charcoal hover:underline">
                    {d('leave', 'Leave conversation')}
                  </button>
                )}
                {isOwner && (
                  <button type="button" disabled={busy} onClick={async () => { if (await confirm({ title: d('archive', 'Archive conversation'), message: d('archive_confirm', 'Archive this conversation for everyone? Messages are kept but nobody can post.'), danger: true, confirmLabel: d('archive', 'Archive conversation') })) run(async () => { await conversationsApi.archive(conversation.id); onLeftOrArchived?.(); }, d('archived', 'Conversation archived')); }} className="text-left text-sm text-red-600 hover:underline">
                    {d('archive', 'Archive conversation')}
                  </button>
                )}
                <p className="text-[11px] text-muted-foreground">{d('delete_unavailable', 'Deleting conversations is not available; archive instead.')}</p>
              </div>
            </div>
          </section>
        )}
      </div>

      <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} />
    </div>
  );
};
